// src/components/Billing/PaymentHistory.js  (or wherever you place it)
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from "../../../utils/axiosInstance";
import backendURL from '../../../config'; // Your backend URL config
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper, // Wrap TableContainer in Paper for elevation/border
    Link as MuiLink, // Use MuiLink for external invoice links
    Chip,
    Tooltip, // Optional: for showing full error on hover
    IconButton // Optional: for invoice button
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt'; // Example icon for invoice
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; // Example icon for errors

// Helper functions (assuming these are accessible or defined here)
const formatCurrency = (amount, currencyCode = 'usd') => {
    if (typeof amount !== 'number') return 'N/A'; // Handle non-numbers
    try {
         // Assuming amount is in smallest unit (e.g., cents) - ADJUST IF NOT
         // If amount is already in dollars/euros, remove the division by 100
         // Example: return new Intl.NumberFormat(...).format(amount);
         return new Intl.NumberFormat('en-US', {
             style: 'currency',
             currency: currencyCode?.toUpperCase() || 'USD',
         }).format(amount / 100); // Divide if amount is in cents/pence etc.
    } catch(e) {
         console.warn("Currency formatting failed:", e);
         // Fallback assumes amount is in smallest unit
         return `$${(amount / 100).toFixed(2)}`;
    }
 };

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        // Format to include time as well, might be useful for transactions
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
        });
    } catch (e) {
        console.warn("Date formatting failed:", e);
        return dateString; // Fallback
    }
};

const PaymentHistory = () => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        setError(null);
        setHistory([]); // Clear previous history

        
        try {
            const response = await axiosInstance.get(`/billing-history`);

            console.log("Billing History Response:", response.data);

            // Expecting the backend to return the list directly in response.data
            if (Array.isArray(response.data)) {
                setHistory(response.data);
            } else {
                console.warn("Billing history API did not return an array:", response.data);
                // Handle cases where the backend might wrap the array, e.g., { data: [...] }
                if (response.data && Array.isArray(response.data.data)) {
                     setHistory(response.data.data);
                } else {
                     setError("Received unexpected data format for billing history.");
                }
            }

        } catch (err) {
            console.error("Error fetching billing history:", err);
            if (err.response) {
                if (err.response.status === 401) {
                    setError("Your session may have expired. Please log in again.");
                } else {
                    setError(err.response.data?.message || "Failed to load billing history.");
                }
            } else {
                 setError("Network error: Could not connect to server.");
            }
        } finally {
            setLoading(false);
        }
    }, []); // No dependencies needed here

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]); // Run fetch on mount

    // --- Render Helper for Status Chip ---
    const renderStatusChip = (status) => {
        let color = 'default';
        let label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'; // Capitalize

        switch (status) {
            case 'succeeded':
                color = 'success';
                break;
            case 'failed':
                color = 'error';
                break;
            case 'pending': // If you have pending status
                color = 'warning';
                break;
            // Add more cases if needed
        }
        return <Chip label={label} color={color} size="small" />;
    };

    // --- Render Content ---
    const renderContent = () => {
        if (loading) {
            return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
        }

        if (error) {
            return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
        }

        if (history.length === 0) {
            return <Typography sx={{ p: 3, textAlign: 'center' }}>No payment history found.</Typography>;
        }

        return (
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="transaction history table">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                            <TableCell>Date</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Card</TableCell>
                            <TableCell align="center">Receipt</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {history.map((tx) => (
                            <TableRow
                                key={tx.id}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell component="th" scope="row">
                                    {formatDate(tx.date)}
                                </TableCell>
                                <TableCell>{tx.description || 'N/A'}</TableCell>
                                <TableCell align="right">
                                    {formatCurrency(tx.amount, tx.currency)}
                                </TableCell>
                                <TableCell>
                                    {renderStatusChip(tx.status)}
                                    {tx.status === 'failed' && tx.error && (
                                        <Tooltip title={tx.error} arrow>
                                            <ErrorOutlineIcon
                                                fontSize="small"
                                                color="error"
                                                sx={{ verticalAlign: 'middle', ml: 0.5, cursor: 'help' }}
                                            />
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {tx.card_brand && tx.card_last4
                                        ? `${tx.card_brand.charAt(0).toUpperCase() + tx.card_brand.slice(1)} **** ${tx.card_last4}`
                                        : 'N/A'}
                                </TableCell>
                                <TableCell align="center">
                                    {tx.invoice_url ? (
                                        <Tooltip title="View Receipt" arrow>
                                             <IconButton
                                                 component={MuiLink} // Use MUI Link for external URL
                                                 href={tx.invoice_url}
                                                 target="_blank" // Open in new tab
                                                 rel="noopener noreferrer" // Security best practice
                                                 size="small"
                                                 color="primary"
                                             >
                                                 <ReceiptIcon />
                                             </IconButton>
                                        </Tooltip>
                                    ) : (
                                        '-' // Placeholder if no invoice
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };


    return (
         <Card variant="outlined"> {/* Or use Box directly */}
            <CardContent>
                 <Typography variant="h5" component="div" gutterBottom sx={{ mb: 2 }}>
                     Payment History
                 </Typography>
                 {renderContent()}
             </CardContent>
         </Card>
    );
};

export default PaymentHistory;